/**
 * Phone Number Formatting Utilities
 * Stores only digits in database, displays formatted in UI/PDF
 */

/**
 * Formats a phone number for display (UI/PDF) to (XXX) XXX-XXXX format
 * @param {string} value - The phone number (digits only or any format)
 * @returns {string} - Formatted phone number for display
 */
export const formatPhoneForDisplay = (value) => {
  if (!value) return '';

  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Don't format if less than 10 digits
  if (digits.length < 10) return digits;

  // Format 10-digit US numbers as (XXX) XXX-XXXX
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Format 11+ digit international numbers as +X (XXX) XXX-XXXX
  if (digits.length > 10) {
    const countryCode = digits.slice(0, digits.length - 10);
    const areaCode = digits.slice(-10, -7);
    const exchange = digits.slice(-7, -4);
    const number = digits.slice(-4);
    return `+${countryCode} (${areaCode}) ${exchange}-${number}`;
  }

  return digits;
};

/**
 * Formats a phone number for input field (real-time formatting as user types)
 * @param {string} value - The input phone number
 * @returns {string} - Formatted phone number for input
 */
export const formatPhoneForInput = (value) => {
  if (!value) return '';

  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Format based on length for better UX while typing
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else {
    // For numbers longer than 10 digits, format as +X (XXX) XXX-XXXX
    const countryCode = digits.slice(0, digits.length - 10);
    const areaCode = digits.slice(-10, -7);
    const exchange = digits.slice(-7, -4);
    const number = digits.slice(-4);
    return `+${countryCode} (${areaCode}) ${exchange}-${number}`;
  }
};

/**
 * Validates if a phone number is in a valid format
 * @param {string} phoneNumber - The phone number to validate
 * @returns {object} - {isValid: boolean, message: string}
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return { isValid: true, message: '' }; // Empty is valid (optional field)
  }

  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');

  // Check if contains only digits and formatting characters
  const allowedChars = /^[\d\s\-()+.]*$/;
  if (!allowedChars.test(phoneNumber)) {
    return { isValid: false, message: 'Phone number contains invalid characters' };
  }

  // Check digit count
  if (digits.length < 10) {
    return { isValid: false, message: 'Phone number must have at least 10 digits' };
  }

  if (digits.length > 15) {
    return { isValid: false, message: 'Phone number cannot exceed 15 digits' };
  }

  // Valid US number (10 digits)
  if (digits.length === 10) {
    return { isValid: true, message: '' };
  }

  // Valid international number (11-15 digits)
  if (digits.length >= 11 && digits.length <= 15) {
    return { isValid: true, message: '' };
  }

  return { isValid: false, message: 'Invalid phone number format' };
};

/**
 * Legacy function for backward compatibility
 * @param {string} phoneNumber - The phone number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidPhoneNumber = (phoneNumber) => {
  return validatePhoneNumber(phoneNumber).isValid;
};

/**
 * Extracts only digits from a phone number
 * @param {string} phoneNumber - The phone number
 * @returns {string} - Only the digits
 */
export const getPhoneDigits = (phoneNumber) => {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/\D/g, '');
};

/**
 * Cleans and formats a phone number for storage
 * @param {string} phoneNumber - The phone number to clean
 * @returns {string} - Cleaned and formatted phone number
 */
export const cleanPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  const digits = getPhoneDigits(phoneNumber);
  
  // If it's a valid US number (10 digits), format it
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  // If it's an international number (11+ digits), format with country code
  if (digits.length > 10) {
    const countryCode = digits.slice(0, digits.length - 10);
    const areaCode = digits.slice(-10, -7);
    const exchange = digits.slice(-7, -4);
    const number = digits.slice(-4);
    return `+${countryCode} (${areaCode}) ${exchange}-${number}`;
  }
  
  // If less than 10 digits, return as-is (might be incomplete)
  return phoneNumber;
};

/**
 * Handles phone number input change with real-time formatting
 * @param {string} value - The input value
 * @param {function} onChange - The onChange handler
 * @param {string} name - The field name
 */
export const handlePhoneInputChange = (value, onChange, name) => {
  const formatted = formatPhoneForInput(value);

  // Create synthetic event to match expected format
  const syntheticEvent = {
    target: {
      name: name,
      value: formatted
    }
  };

  onChange(syntheticEvent);
};

/**
 * Centralized phone validation for form submissions
 * @param {string} phoneNumber - The phone number to validate
 * @param {string} fieldName - The field name for error messages
 * @returns {string|null} - Error message or null if valid
 */
export const validatePhoneForSubmission = (phoneNumber, fieldName) => {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return null; // Empty is valid (optional field)
  }

  const digits = phoneNumber.replace(/\D/g, '');
  if (digits.length < 10) {
    return `${fieldName} must have at least 10 digits (${digits.length} entered)`;
  }

  if (digits.length > 15) {
    return `${fieldName} cannot exceed 15 digits (${digits.length} entered)`;
  }

  return null; // Valid
};

/**
 * Formats phone number for FDLE form submission
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number for FDLE (XXX-XXX-XXXX format)
 */
export const formatPhoneForFDLE = (phoneNumber) => {
  if (!phoneNumber) return '';

  const digits = getPhoneDigits(phoneNumber);

  // FDLE expects XXX-XXX-XXXX format for US numbers
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // For international numbers, use +X-XXX-XXX-XXXX format
  if (digits.length > 10) {
    const countryCode = digits.slice(0, digits.length - 10);
    const areaCode = digits.slice(-10, -7);
    const exchange = digits.slice(-7, -4);
    const number = digits.slice(-4);
    return `+${countryCode}-${areaCode}-${exchange}-${number}`;
  }

  // If less than 10 digits, return as-is with dashes where possible
  if (digits.length >= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}${digits.length > 6 ? '-' + digits.slice(6) : ''}`;
  } else if (digits.length >= 3) {
    return `${digits.slice(0, 3)}${digits.length > 3 ? '-' + digits.slice(3) : ''}`;
  }

  return digits;
};

/**
 * Cleans phone number for database storage (digits only)
 * @param {string} phoneNumber - The phone number to clean
 * @returns {string} - Cleaned phone number (digits only)
 */
export const cleanPhoneForDatabase = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove all non-digit characters
  return phoneNumber.replace(/\D/g, '');
};

/**
 * Validates email format
 * @param {string} email - The email address to validate
 * @returns {object} - {isValid: boolean, message: string}
 */
export const validateEmailFormat = (email) => {
  if (!email || email.trim() === '') {
    return { isValid: true, message: '' }; // Empty is valid for optional fields
  }

  // Comprehensive email regex pattern
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(email.trim())) {
    return { isValid: false, message: 'Please enter a valid email address (e.g., officer@agency.gov)' };
  }

  return { isValid: true, message: '' };
};

/**
 * Validates email for form submission
 * @param {string} email - The email address to validate
 * @param {string} fieldName - The field name for error messages
 * @returns {string|null} - Error message or null if valid
 */
export const validateEmailForSubmission = (email, fieldName) => {
  if (!email || email.trim() === '') {
    return null; // Empty is valid for optional fields
  }

  const validation = validateEmailFormat(email);
  if (!validation.isValid) {
    return `${fieldName} must be a valid email address`;
  }

  return null; // Valid
};
